import { useEffect, useState } from 'react';
import { logger } from '../../services/logger';
import { useStore } from '../useStore';
import { contestSelections } from '../../stores/contest-selections';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { FoContest, ShortFoContest } from '@staycool/contests-types/contest';
import { getContest, getContestsList } from '../../microservices/contests';
import { PredictionStatus } from '@staycool/contests-types/prediction';

export function useFetchContests(userId?: string) {
    const [contests, setContests] = useState<ShortFoContest[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const { rows } = await getContestsList();
                setContests(rows);
            } catch (error) {
                logger.error('useFetchContests', 'fetchData', error);
                setContests([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [userId]);

    return { isLoading, contests };
}

export function useFetchContest(contestId) {
    const [contest, setContest] = useState<FoContest>();
    const [isLoading, setIsLoading] = useState(false);
    const [, setEntries] = useStore(contestSelections.entries);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                if (!contestId) {
                    setIsLoading(false);
                    return null;
                }
                const contest = await getContest(contestId);
                setIsLoading(false);
                if (contest) {
                    setContest(contest);
                    setEntries(contest.entries || []);
                }
            } catch (error) {
                logger.error('useFetchContest', 'fetchData', error);
                setIsLoading(false);
                setContest(undefined);
            }
        };

        fetchData();
    }, [contestId]);
    return { isLoading, contest };
}

export function useContestPreSelectionState() {
    const [initialSelections, setInitialSelections] = useStore(contestSelections.initialSelections);
    const [selections, setSelections] = useStore(contestSelections.selections);
    const [activeEntry, setActiveEntry] = useStore(contestSelections.activeEntry);
    const [entries, setEntries] = useStore(contestSelections.entries);
    const [activeLegId] = useStore(contestSelections.activeLegId);

    const isResubmit = Boolean(
        activeEntry &&
            !isEmpty(initialSelections[activeEntry.id]) &&
            !isEqual(initialSelections[activeEntry.id], selections[activeEntry.id]),
    );

    function addSelection(marketId: number, outcomeId: number, matchId: number) {
        if (!activeEntry || ['WON', 'ELIMINATED', 'DROPPED_OUT'].includes(activeEntry.status)) {
            return;
        }

        const { id } = activeEntry;
        const purchasedSelection = initialSelections[id];
        if (
            purchasedSelection?.length &&
            purchasedSelection.some((prediction) => prediction.status && prediction.status !== PredictionStatus.PENDING)
        ) {
            return;
        }
        let selectionsByEntry = selections[id] ?? [];

        if (isEqual(initialSelections[id], selectionsByEntry)) {
            selectionsByEntry = [];
        }

        const selectionIndex = selectionsByEntry.findIndex((selection) => selection.marketId === marketId);

        if (selectionIndex > -1) {
            if (selectionsByEntry[selectionIndex].outcomeId === outcomeId) {
                removeSelection(marketId);
                return;
            }
            selectionsByEntry[selectionIndex].outcomeId = outcomeId;
        } else {
            selectionsByEntry.push({ marketId, outcomeId, matchId, legId: activeLegId! });
        }

        setSelections({ ...selections, [id]: selectionsByEntry });
    }

    function removeSelection(marketId: number) {
        if (!activeEntry) {
            return;
        }

        const { id } = activeEntry;
        const filteredSelections = selections[id].filter((selection) => selection.marketId !== marketId);

        setSelections({
            ...selections,
            [id]: filteredSelections,
        });
    }

    function clearSelections() {
        if (activeEntry) {
            setSelections({
                ...selections,
                [activeEntry.id]: cloneDeep(initialSelections[activeEntry.id]) || [],
            });
        }
    }

    function getSelectionsByMarketId(marketId: number) {
        if (activeEntry) {
            return selections[activeEntry.id]?.find((sel) => sel.marketId === marketId);
        }
    }

    function getSelections(): { marketId: number; outcomeId: number; matchId: number }[] {
        if (activeEntry) {
            return selections[activeEntry.id] || [];
        }

        return [];
    }

    function getPredictionsForSend() {
        if (activeEntry && selections[activeEntry.id]) {
            return selections[activeEntry.id].map((selection) => {
                return {
                    market_id: selection.marketId,
                    match_id: selection.matchId,
                    outcome_id: selection.outcomeId,
                    entry_id: activeEntry.id,
                };
            });
        }
        return [];
    }

    return {
        activeEntry,
        setActiveEntry,
        entries,
        setEntries,
        initialSelections,
        setInitialSelections,
        selections,
        setSelections,
        addSelection,
        removeSelection,
        getSelectionsByMarketId,
        getSelections,
        clearSelections,
        isResubmit,
        getPredictionsForSend,
    };
}
